import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import { useGetMisbilledUnits, useGetMisbilledUnitsProducts } from '@/hooks/service-hooks/billingTools';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { getMisbilledUnits } from '@/services/billingTools';
import type { BillingToolsProducts, MisbilledUnits } from '@/types/billingTools';
import type { FilterType } from '@/types/table';
import { formatDateToString } from '@/utils/dateUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { missBilledUnitsColumns } from '../billingTools';

const CLINIC_KEY = 'clinic';

/**
 * MisbilledUnitsTable Component
 *
 * Displays transactions where the billed units do not match the expected units for a product.
 * Helps identify unit billing discrepancies that need correction.
 *
 * Features:
 * - Real-time display of misbilled unit transactions
 * - Shows product, patient info, clinic, physician, and unit discrepancies
 * - Filterable by clinic
 * - Export functionality (CSV, Excel)
 * - Integrated with app tour system
 */
const MisbilledUnitsTable = () => {
	// Shared clinic filter helpers (key used by Misbilled Units API)
	const { defaultClinicNames, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);
		const [products, setProducts] = useState<BillingToolsProducts[]>([]);

	// Table filter state: manages pagination, search, sorting, and tab selection
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		globalFilters: JSON.stringify(
			defaultClinicNames.length
				? [
					{
						key: CLINIC_KEY,
						values: defaultClinicNames,
					},
				]
				: [],
		),
		search: '',
		enabled: defaultClinicNames.length > 0,
		tab: '',
		sort: '',
	});

	const columns = useMemo(() => missBilledUnitsColumns, []);

	// Fetch invoices data based on current filters
	// IMPORTANT: Only disable the query when tutorial is ACTIVE
	// When tutorial is NOT active, enable the query to fetch real data
	const { data, isFetching } = useGetMisbilledUnits({
		...filters,
		enabled: JSON.parse(filters.globalFilters || '[]').length > 0,
	});

	// State and effect to fetch products for product filter options
	const { data: productsData } = useGetMisbilledUnitsProducts({
		globalFilters: JSON.stringify([
			{
				key: CLINIC_KEY,
				values: defaultClinicNames,
			},
		]),
		enabled: defaultClinicNames.length > 0 && products.length === 0,
	});

	useEffect(() => {
		if (productsData?.items?.length && !products.length) {
			setProducts(productsData.items);
		}
	}, [products.length, productsData]);

	/**
	 * Transform misbilled units data for table display
	 * Converts raw API data into table-ready format with:
	 * - Formatted procedure dates
	 * - Product and patient information
	 * - Clinic and physician details
	 * - Unit discrepancies (units vs sumofunits)
	 */
	const transformedData = useMemo(() => {
		if (!data?.items) return [];
		const misbilledHCPCSData = data.items;

		return misbilledHCPCSData.map((item) => {
			return {
				id: item.tatransactionid,
				clinic: item.clinic ? (
					<FaceliftTruncatedTextWithTooltip text={item.clinic} maxLength={30} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={30} />
				),
				PhysicianName: item.PhysicianName ? (
					<FaceliftTruncatedTextWithTooltip text={item.PhysicianName} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				patientName: item.patientName ? (
					<FaceliftTruncatedTextWithTooltip text={item.patientName} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				qcode: item.qcode || '--',
				Product: item.Product ? (
					<FaceliftTruncatedTextWithTooltip text={item.Product} maxLength={25} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={25} />
				),
				units: item.units ?? '--',
				sumofunits: item.sumofunits ?? '--',
				emrid: item.emrid ? (
					<FaceliftTruncatedTextWithTooltip text={item.emrid} maxLength={15} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={15} />
				),
				proceduredos: item.proceduredos ? formatDateToString(new Date(item.proceduredos)) : '--',
				tatransactionid: item.tatransactionid ? (
					<FaceliftTruncatedTextWithTooltip text={String(item.tatransactionid)} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
			};
		});
	}, [data]);

	/**
	 * Transform misbilled units data for export
	 * Converts data to plain text format for CSV/Excel compatibility
	 */
	const transformedDataToExport = (misbilledUnitsData: MisbilledUnits[]) => {
		if (!misbilledUnitsData.length) return [];

		return misbilledUnitsData.map((item) => {
			return {
				id: item.tatransactionid,
				clinic: item.clinic || '--',
				PhysicianName: item.PhysicianName || '--',
				patientName: item.patientName || '--',
				qcode: item.qcode || '--',
				Product: item.Product || '--',
				units: item.units ?? '--',
				sumofunits: item.sumofunits ?? '--',
				emrid: item.emrid || '--',
				proceduredos: item.proceduredos ? formatDateToString(new Date(item.proceduredos)) : '--',
				tatransactionid: item.tatransactionid || '--',
			};
		});
	};

	/**
	 * Handle filter changes from DataTable
	 * Updates filter state to trigger new data fetch with applied filters
	 */
	const onFilterApply = useCallback(
		({
			page,
			limit,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters, with clinic names in this case
			const mappedFilters = mapGlobalFilters(globalFilters || [], true);

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				globalFilters: JSON.stringify(mappedFilters),
				search,
				enabled: defaultClinicNames.length > 0,
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[mapGlobalFilters],
	);

	/**
	 * Fetch all misbilled units records for export
	 * Iterates through all pages to get complete dataset with current filters applied
	 * @returns Complete misbilled units data formatted for export
	 */
	const fetchAllDataToExport = async () => {
		let allItems: MisbilledUnits[] = [];
		let page = 1;
		let hasMore = true;

		while (hasMore) {
			const response = await getMisbilledUnits(
				page,
				9999,
				'[]',
				filters.search,
				(filters.status as string) || '',
			);

			if (response?.items?.length) {
				allItems = allItems.concat(response.items);
				const totalItems = response?.meta?.totalItems || 0;
				hasMore = allItems.length < totalItems;
				page += 1;
			} else {
				hasMore = false;
			}
		}

		const misbilledHCPCSDataForExcel = { items: allItems };
		const transformedData = transformedDataToExport(
			misbilledHCPCSDataForExcel.items || [],
		);

		return transformedData;
	};

	/**
	 * Export misbilled units data to CSV file
	 */
	const exportAllAsCSV = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateCSVFromTable(
				columns,
				transformedInvoiceData,
				`MisbilledUnits-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	/**
	 * Export misbilled units data to Excel file
	 */
	const exportAllAsExcel = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateExcelFromTable(
				columns,
				transformedInvoiceData,
				`MisbilledUnits-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			borderRadius="md"
			boxShadow="sm"
		>
			<DataTable
				tableName="Misbilled Units"
				loading={isFetching}
				items={transformedData}
				columns={columns}
				topLabel="Misbilled Units"
				totalCount={data?.meta.totalItems || 0}
				onFilterApply={onFilterApply}
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				globalFilter={{
					title: 'Filters',
					items: [
						{
							options: defaultClinicNames.map((clinic) => ({
								label: clinic,
								id: clinic,
							})),
							key: 'Clinics',
							name: 'Clinics',
						},
						{
							options: products.map((product) => ({
								label: product.Product,
								id: product.Product,
							})),
							key: 'Product',
							name: 'Products',
						},
					],
				}}
				actions={[]}
				exportable
				hideAllStatusFilter={true}
				csv
				excel
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>
		</Box>
	);
};

export default MisbilledUnitsTable;
